import { create } from 'zustand';

/**
 * Variable scopes: environments + the global/workspace/collection variable sets.
 *
 * Replaces the App-level useState + the four ref-mirrors (selectedEnvironmentRef,
 * workspaceVariablesRef, globalVariablesRef, collectionVariablesByIdRef) that
 * existed so async handlers (AI actions, request send) could read the latest
 * values — `useVariablesStore.getState()` is the synchronous equivalent.
 *
 * Setters are exported as stable module-level functions (named identically to
 * the old useState setters) so App call sites are unchanged and lint-clean.
 * Setters mirror React useState semantics (value or updater function).
 */
const initialState = {
  environments: [],
  selectedEnvironment: null,
  workspaceVariables: [],
  globalVariables: [],
  collectionVariablesById: {},
};

const make = (set, key) => (value) =>
  set((state) => ({ [key]: typeof value === 'function' ? value(state[key]) : value }));

export const useVariablesStore = create((set) => ({
  ...initialState,
  setEnvironments: make(set, 'environments'),
  setSelectedEnvironment: make(set, 'selectedEnvironment'),
  setWorkspaceVariables: make(set, 'workspaceVariables'),
  setGlobalVariables: make(set, 'globalVariables'),
  setCollectionVariablesById: make(set, 'collectionVariablesById'),
  reset: () => set({ ...initialState }),
}));

/** Resolved variable list (env → workspace → global) shared by all consumers. */
export function selectResolvedVariables(state) {
  return [
    ...(state.selectedEnvironment?.variables || []),
    ...(state.workspaceVariables || []),
    ...(state.globalVariables || []),
  ];
}

// Stable module-level setters (delegate to the store actions).
export const setEnvironments = (v) => useVariablesStore.getState().setEnvironments(v);
export const setSelectedEnvironment = (v) => useVariablesStore.getState().setSelectedEnvironment(v);
export const setWorkspaceVariables = (v) => useVariablesStore.getState().setWorkspaceVariables(v);
export const setGlobalVariables = (v) => useVariablesStore.getState().setGlobalVariables(v);
export const setCollectionVariablesById = (v) => useVariablesStore.getState().setCollectionVariablesById(v);
